import { observer } from "mobx-react";
// student env specific navigation menu
import type React from "react";
import { useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { useProduct } from "hooks/useProduct";

import { buildUrlWithPathParams } from "_helpers/utils/urlBuilder";

import { NavigationMenu } from "components/NavigationMenu/NavigationMenu";
import type { NavigationMenuItem } from "components/NavigationMenu/NavigationMenu.model";
import { TestType } from "constants/exam-constants";
import {
  PLACEHOLDER_PAGE_ROUTES,
  PLAYER_POC_ROUTES,
  PRODUCT_PAGES_ROUTES,
  RESULT_PAGE_ROUTES,
  TEST_ENVIRONMENT_ROUTES,
  TEST_PAGE_ROUTES,
} from "constants/routes";

import { STUDENT_MODULES } from "analytics/constants/pageTypes";
import { PLATFORM_EVENT_TYPES } from "analytics/constants/platformEventTypes";
import { FontAwesomeIcon } from "components/FontAwesomeIcon";
import {
  StyledDropdownIcon,
  StyledIcon,
  StyledNavigationItemName,
  StyledOrphanNavigationItemName,
} from "components/NavigationMenu/NavigationMenu.styled";
import { ProductType } from "constants/hst-constants";
import {
  HstProductsWithPlaceholderModules,
  getComingSoonModules,
} from "constants/placeholder-constants";
import { useFeatureFlags } from "hooks/useFeatureFlags";
import { useModuleAvailability } from "hooks/useModuleAvailability";
import type { User, UserUpdate } from "models/user/User";
import { useLocation } from "react-router-dom";
import { Box, CssFlex } from "styles/helpers/layout";
import { hogesTheme } from "themes/hoges";

interface ComponentProps {
  className?: string;
  dataCy?: string;
  userDetails?: User;
  sideMenuEnabled?: boolean;
  onLogout: () => void;
  toggleSideMenu: () => void;
  updateUserDetails: (userDetails: UserUpdate) => void;
}

export const StudentNavigationMenu: React.FC<ComponentProps> = observer(({ className, dataCy }) => {
  const { t } = useTranslation("homepage");
  const location = useLocation();

  const { productDetails: product, productsList: ownedProducts, fetchProducts } = useProduct();
  const isStudentABusinessUser =
    ownedProducts?.length &&
    ownedProducts?.every((product) => product.type === ProductType.BUSINESS);
  const { studentNavigationPersonalProgress, showComingSoonModules, showPlayerPocPage } =
    useFeatureFlags();

  const getUrlWithProductId = useCallback(
    (placeholderUrl: string) =>
      buildUrlWithPathParams(placeholderUrl, { productId: product?.id as number }),
    [product],
  );

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const { isAdaptiveModuleAvailable, isWritingModuleAvailable } = useModuleAvailability();

  const isTestsExpanded =
    location.pathname.includes("/diagnostic-test") || location.pathname.startsWith("/test");
  const isResultsExpanded =
    location.pathname.includes("/results") || location.pathname.includes("/certificates");

  const newMenuItems = useMemo<NavigationMenuItem[]>(
    () => [
      {
        id: "student-dashboard",
        dataCy: "student-dashboard",
        nestingLevel: 0,
        title: (
          <StyledOrphanNavigationItemName>
            <StyledIcon iconName="far fa-home" iconSize="lg" />
            {t("sideMenu.dashboard", "Dashboard")}
          </StyledOrphanNavigationItemName>
        ),
        redirectPath:
          ownedProducts?.length === 0
            ? PRODUCT_PAGES_ROUTES.NO_PRODUCT_LICENSE
            : getUrlWithProductId(PRODUCT_PAGES_ROUTES.PRODUCT_DETAILS),
        onlyActiveOnExactPath: true,
      },
      /* Show module as standalone item if product has only one; otherwise we wrap them into a category */
      ...(product?.modules && product.modules.length === 1
        ? [
            {
              id: `product-module-${product.modules[0].id}`,
              title: (
                <StyledNavigationItemName>
                  <StyledIcon iconName="far fa-dot-circle" iconSize="lg" />
                  {product.modules[0].title}
                </StyledNavigationItemName>
              ),
              dataCy: "product-module-item",
              redirectPath: buildUrlWithPathParams(PRODUCT_PAGES_ROUTES.PRODUCT_MODULE_DETAILS, {
                productId: product.id,
                moduleId: product.modules[0].id,
              }),
              nestingLevel: 0,
              excludeFromActivePath: [
                TEST_PAGE_ROUTES.DIAGNOSTIC_TEST_GROUPS,
                TEST_PAGE_ROUTES.DIAGNOSTIC_TEST_LIST,
                TEST_PAGE_ROUTES.DIAGNOSTIC_TEST_DETAILS,
              ],
            },
          ]
        : [
            {
              id: "product-modules",
              dataCy: "product-module-list",
              expandedByDefault: true,
              title: (
                <StyledNavigationItemName>
                  <StyledIcon iconName="far fa-dot-circle" />
                  {t("sideMenu.modules.title", "Modules")}
                  <StyledDropdownIcon iconName="far fa-angle-down" iconSize="lg" />
                </StyledNavigationItemName>
              ),
              subItems: [
                ...(product?.modules?.flatMap((module) => {
                  const defaultModuleMenuItem = {
                    id: `product-module-${module.id}`,
                    title: module.title,
                    nestingLevel: 1,
                    dataCy: "module-list-item",
                    redirectPath: buildUrlWithPathParams(
                      PRODUCT_PAGES_ROUTES.PRODUCT_MODULE_DETAILS,
                      {
                        productId: product.id,
                        moduleId: module.id,
                      },
                    ),
                    excludeFromActivePath: [
                      TEST_PAGE_ROUTES.DIAGNOSTIC_TEST_GROUPS,
                      TEST_PAGE_ROUTES.DIAGNOSTIC_TEST_LIST,
                      TEST_PAGE_ROUTES.DIAGNOSTIC_TEST_DETAILS,
                    ],
                  };
                  return [defaultModuleMenuItem];
                }) || []),
                // Coming soon modules
                ...(showComingSoonModules &&
                product?.id &&
                HstProductsWithPlaceholderModules.includes(product.id)
                  ? getComingSoonModules(t).map((module) => ({
                      id: `coming-soon-module-${module.type}`,
                      title: (
                        <CssFlex
                          gap={0.5}
                          alignItems="center"
                          justifyContent="space-between"
                          paddingRight="0.8rem"
                          width="100%"
                        >
                          {module.title}
                          <Box
                            background={hogesTheme.palette.red._950}
                            borderRadius={100}
                            padding="0.5rem"
                            height="1.5rem"
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                          >
                            <FontAwesomeIcon
                              iconName="far fa-hourglass"
                              iconSize="sm"
                              style={{
                                color: hogesTheme.palette.neutral._200,
                                fontSize: "12px",
                              }}
                            />
                          </Box>
                        </CssFlex>
                      ),
                      nestingLevel: 1,
                      dataCy: `coming-soon-${module.type}-module-item`,
                      redirectPath: buildUrlWithPathParams(
                        PLACEHOLDER_PAGE_ROUTES.COMING_SOON_MODULE,
                        {
                          productId: product.id,
                          moduleType: module.type,
                        },
                      ),
                    }))
                  : []),
              ],
            },
          ]),
      {
        id: "test-list",
        dataCy: "test-list",
        expandedByDefault: isTestsExpanded,
        title: (
          <StyledNavigationItemName>
            <StyledIcon iconName="far fa-flag" iconSize="lg" />
            {t("sideMenu.test.title", "Tests")}
            <StyledDropdownIcon iconName="far fa-angle-down" iconSize="lg" />
          </StyledNavigationItemName>
        ),
        subItems: [
          ...(product?.modules[0]?.hasDiagnosticTest
            ? [
                {
                  id: `product-module-${product?.modules[0].id}-diagnostic-test`,
                  title: t("sideMenu.modules.diagnosticTest"),
                  dataCy: `module-${product?.modules[0].id}-diagnostic-test`,
                  redirectPath: buildUrlWithPathParams(TEST_PAGE_ROUTES.DIAGNOSTIC_TEST_GROUPS, {
                    productId: product.id,
                    moduleId: product?.modules[0].id,
                  }),
                  nestingLevel: 1,
                  eventTrackData: {
                    module: STUDENT_MODULES.BASIC_PROGRESS,
                    itemId:
                      PLATFORM_EVENT_TYPES.STUDENT_MODULES_EVENT_TYPES.CLICK_GO_TO_PRACTICE_TEST,
                    elementId: `student-nav-menu-module-${product?.modules[0].id}-diagnostic-test`,
                  },
                },
              ]
            : []),
          {
            nestingLevel: 1,
            id: "go-test-item",
            title: t("sideMenu.test.items.goToTestEnv", "Go to test"),
            dataCy: "go-test-item",
            redirectPath: TEST_ENVIRONMENT_ROUTES.LANDING_PAGE,
            excludeFromActivePath: [
              TEST_PAGE_ROUTES.DIAGNOSTIC_TEST_GROUPS,
              TEST_PAGE_ROUTES.DIAGNOSTIC_TEST_LIST,
              TEST_PAGE_ROUTES.DIAGNOSTIC_TEST_DETAILS,
            ],
            eventTrackData: {
              module: STUDENT_MODULES.DASHBOARD,
              itemId: PLATFORM_EVENT_TYPES.STUDENT_DASHBOARD_EVENT_TYPES.CLICK_GO_TEST_ENVIRONMENT,
              elementId: "student-nav-menu-go-to-test-env",
            },
          },
        ],
      },
      {
        id: "result-list",
        dataCy: "result-list",
        expandedByDefault: isResultsExpanded,
        title: (
          <StyledNavigationItemName>
            <StyledIcon iconName="fas fa-chart-line" iconSize="lg" />
            {t("sideMenu.results.title", "Results")}
            <StyledDropdownIcon iconName="far fa-angle-down" iconSize="lg" />
          </StyledNavigationItemName>
        ),
        subItems: [
          ...(ownedProducts && ownedProducts.length > 0
            ? [
                ...(studentNavigationPersonalProgress && isAdaptiveModuleAvailable
                  ? [
                      {
                        id: "results-personal-progress",
                        title: t("sideMenu.results.items.personalProgress", "Basic skills"),
                        dataCy: "results-personal-progress",
                        redirectPath: getUrlWithProductId(RESULT_PAGE_ROUTES.PERSONAL_PROGRESS),
                        nestingLevel: 1,
                      },
                    ]
                  : []),
              ]
            : []),
          ...(!isStudentABusinessUser
            ? [
                {
                  id: "results-freebie-test",
                  dataCy: "results-freebie-test",
                  nestingLevel: 1,
                  title: t("sideMenu.results.items.freebieTest", "Freebie Test"),
                  redirectPath: buildUrlWithPathParams(RESULT_PAGE_ROUTES.TEST_TYPE_RESULTS_LIST, {
                    testType: TestType.INTAKE_TEST,
                  }),
                },
                {
                  id: "results-formative-test",
                  dataCy: "results-formative-test",
                  nestingLevel: 1,
                  title: t("sideMenu.results.items.formativeTest", "Formative Test"),
                  redirectPath: buildUrlWithPathParams(RESULT_PAGE_ROUTES.TEST_TYPE_RESULTS_LIST, {
                    testType: TestType.FORMATIVE_TEST,
                  }),
                },
                {
                  id: "results-summative-test",
                  dataCy: "results-summative-test",
                  nestingLevel: 1,
                  title: t("sideMenu.results.items.summativeTest", "Summative Test"),
                  redirectPath: buildUrlWithPathParams(RESULT_PAGE_ROUTES.TEST_TYPE_RESULTS_LIST, {
                    testType: TestType.SUMMATIVE_TEST,
                  }),
                },
                {
                  id: "certificate-results",
                  dataCy: "certificate-results",
                  nestingLevel: 1,
                  title: t("sideMenu.results.items.certificate", "Certificates"),
                  redirectPath: RESULT_PAGE_ROUTES.OBTAINED_CERTIFICATES,
                },
              ]
            : []),
        ],
      },
      // Show menu item to go to player POC if feature flag is enabled
      // TODO: This can be removed later on, when we do no longer need to hardcode the parameters
      ...(showPlayerPocPage
        ? [
            {
              id: "player-poc-menu-item",
              dataCy: "player-poc-menu-item",
              nestingLevel: 0,
              title: (
                <StyledOrphanNavigationItemName>
                  <StyledIcon iconName="far fa-flask" iconSize="lg" />
                  Player POC
                </StyledOrphanNavigationItemName>
              ),
              redirectPath: buildUrlWithPathParams(PLAYER_POC_ROUTES.CHAPTER_OVERVIEW, {
                packageId: "fdbf1a28-1e7d-4abd-ac9f-7dbe18bbda49",
                chapterId: "6e6f7a4c-ecf2-48f7-ae34-f79c77b3cdc0",
              }),
              onlyActiveOnExactPath: false,
            },
          ]
        : []),
    ],
    [
      t,
      ownedProducts,
      isStudentABusinessUser,
      product,
      getUrlWithProductId,
      isWritingModuleAvailable,
      isAdaptiveModuleAvailable,
      studentNavigationPersonalProgress,
      isTestsExpanded,
      isResultsExpanded,
    ],
  );

  return (
    <NavigationMenu
      showGoToTeacherEnvButton
      className={className}
      dataCy={dataCy}
      menuItems={newMenuItems}
    />
  );
});
